import React, { useState, useEffect } from 'react';
import { supabase, handleSupabaseError, OperationType } from '../supabase';
import {
  CryptoEvent,
  UserProfile,
  UserTier,
  CATEGORY_META,
  CATEGORY_BY_VALUE,
  rowToEvent,
  rowToProfile,
} from '../types';
import {
  Check,
  X,
  Shield,
  Users,
  Calendar as CalendarIcon,
  Building2,
  Search,
  Edit2,
  Trash2,
  Zap,
  ShieldCheck,
  Mail,
  Twitter,
  AlertCircle,
  Clock,
  Info,
  Star,
  TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { EventDetailModal } from './EventDetailModal';
import { AddEventModal } from './AddEventModal';
import { AnalyticsView } from './AnalyticsView';

type AdminTab = 'pending_events' | 'all_events' | 'pending_clients' | 'approved_clients' | 'analytics';

const CHAINS = ['Solana', 'General Crypto', 'Ethereum', 'Bitcoin', 'Base', 'Arbitrum', 'Polygon', 'Fogo'];

const TruncatedText: React.FC<{ text?: string | null; maxLength: number; className?: string }> = ({
  text,
  maxLength,
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const safe = text || '';
  if (safe.length <= maxLength) return <span className={className}>{safe}</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setExpanded((v) => !v);
      }}
      className={`text-left select-none hover:underline ${className || ''}`}
      aria-label={expanded ? 'Collapse text' : 'Expand text'}
    >
      {expanded ? safe : `${safe.substring(0, maxLength)}...`}
    </button>
  );
};

const CategoryPill: React.FC<{ category: string }> = ({ category }) => {
  const meta = CATEGORY_BY_VALUE[category as keyof typeof CATEGORY_BY_VALUE];
  if (!meta) {
    return (
      <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
        {category}
      </span>
    );
  }
  return (
    <span
      className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
      style={{
        backgroundColor: meta.fill,
        color: meta.textColor === 'white' ? '#fff' : '#0b0b0c',
      }}
    >
      {meta.label}
    </span>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    approved: { bg: '#14f195', fg: '#0b0b0c', label: 'Approved' },
    pending: { bg: '#ffd24a', fg: '#0b0b0c', label: 'Pending' },
    rejected: { bg: '#ff6a6a', fg: '#fff', label: 'Rejected' },
  };
  const cfg = map[status];
  if (!cfg) return null;
  return (
    <span
      className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: cfg.bg, color: cfg.fg }}
    >
      {cfg.label}
    </span>
  );
};

const SourceBadge: React.FC<{ source: string }> = ({ source }) => (
  <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
    {source}
  </span>
);

export const AdminPanel: React.FC = () => {
  const [pendingEvents, setPendingEvents] = useState<CryptoEvent[]>([]);
  const [allEvents, setAllEvents] = useState<CryptoEvent[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserProfile[]>([]);
  const [approvedUsers, setApprovedUsers] = useState<UserProfile[]>([]);
  const [activeTab, setActiveTab] = useState<AdminTab>('pending_events');
  const [usersMap, setUsersMap] = useState<Record<string, UserProfile>>({});

  const [eventSearch, setEventSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [clientSearch, setClientSearch] = useState('');

  const [editingEvent, setEditingEvent] = useState<CryptoEvent | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CryptoEvent | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [rejectionModalId, setRejectionModalId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      const [
        { data: pendingEv },
        { data: allEv },
        { data: pendingUs },
        { data: approvedUs },
      ] = await Promise.all([
        supabase.from('events').select('*').eq('status', 'pending').order('starts_at', { ascending: true }),
        supabase.from('events').select('*').order('starts_at', { ascending: false }),
        supabase.from('profiles').select('*').eq('role', 'client').eq('is_approved', false).order('created_at', { ascending: false }),
        supabase.from('profiles').select('*').eq('role', 'client').eq('is_approved', true).order('created_at', { ascending: false }),
      ]);
      setPendingEvents((pendingEv ?? []).map(rowToEvent));
      setAllEvents((allEv ?? []).map(rowToEvent));
      setPendingUsers((pendingUs ?? []).map(rowToProfile));
      setApprovedUsers((approvedUs ?? []).map(rowToProfile));
    };
    void fetchAll();

    const eventsChannel = supabase
      .channel('admin:events')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => void fetchAll())
      .subscribe();
    const profilesChannel = supabase
      .channel('admin:profiles')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => void fetchAll())
      .subscribe();

    return () => {
      void supabase.removeChannel(eventsChannel);
      void supabase.removeChannel(profilesChannel);
    };
  }, []);

  useEffect(() => {
    setUsersMap((prev) => {
      const next = { ...prev };
      let changed = false;
      [...pendingUsers, ...approvedUsers].forEach((u) => {
        if (!next[u.uid]) {
          next[u.uid] = u;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [pendingUsers, approvedUsers]);

  useEffect(() => {
    const uids = Array.from(
      new Set([...pendingEvents, ...allEvents].map((e) => e.createdBy)),
    ).filter((uid): uid is string => !!uid && !usersMap[uid]);
    if (uids.length === 0) return;
    (async () => {
      const { data, error } = await supabase.from('profiles').select('*').in('id', uids);
      if (error || !data) return;
      setUsersMap((prev) => {
        const next = { ...prev };
        for (const row of data) next[row.id] = rowToProfile(row);
        return next;
      });
    })();
  }, [pendingEvents, allEvents, usersMap]);

  const renderSubmitterInfo = (createdBy: string | null, source: string) => {
    // Automated import (no submitter user) → just show the source badge.
    if (!createdBy) {
      return (
        <div className="inline-flex items-center gap-2 flex-wrap">
          <SourceBadge source={source} />
        </div>
      );
    }
    if (createdBy === 'deleted_user') {
      return (
        <div className="inline-flex items-center gap-2 flex-wrap">
          <SourceBadge source={source} />
          <span className="text-[11px] font-medium text-[#0b0b0c]/55 italic">Deleted user</span>
        </div>
      );
    }
    const user = usersMap[createdBy];
    return (
      <div className="inline-flex items-center gap-2 flex-wrap">
        <SourceBadge source={source} />
        {user ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full pill-outline text-[11px] font-bold">
            {user.authProvider === 'twitter' ? (
              <Twitter className="w-3 h-3 text-[#9945ff]" />
            ) : (
              <img src="https://www.google.com/favicon.ico" className="w-3 h-3" alt="Google" />
            )}
            <TruncatedText text={user.displayName} maxLength={18} />
          </span>
        ) : (
          <span className="text-[11px] text-[#0b0b0c]/45 italic">Loading...</span>
        )}
      </div>
    );
  };

  const handleApproveEvent = async (id: string) => {
    try {
      const { error } = await supabase.from('events').update({ status: 'approved' }).eq('id', id);
      if (error) throw error;
    } catch (err) {
      handleSupabaseError(err, OperationType.UPDATE, 'events');
    }
  };

  const handleRejectEvent = async (id: string, reason?: string) => {
    try {
      const updateData: { status: 'rejected'; rejection_reason?: string } = { status: 'rejected' };
      if (reason?.trim()) updateData.rejection_reason = reason.trim();
      const { error } = await supabase.from('events').update(updateData).eq('id', id);
      if (error) throw error;
      setRejectionModalId(null);
      setRejectionReason('');
    } catch (err) {
      handleSupabaseError(err, OperationType.UPDATE, 'events');
    }
  };

  const handleDeleteEvent = async (id: string) => {
    try {
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
      setShowDeleteConfirm(null);
    } catch (err) {
      handleSupabaseError(err, OperationType.DELETE, 'events');
    }
  };

  const handleApproveUser = async (uid: string, tier: UserTier) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_approved: true, tier, approved_at: new Date().toISOString() })
        .eq('id', uid);
      if (error) throw error;
    } catch (err) {
      handleSupabaseError(err, OperationType.UPDATE, 'profiles');
    }
  };

  const handleRevokeUser = async (uid: string) => {
    try {
      const { error } = await supabase.from('profiles').update({ is_approved: false }).eq('id', uid);
      if (error) throw error;
    } catch (err) {
      handleSupabaseError(err, OperationType.UPDATE, 'profiles');
    }
  };

  const handleRejectUser = async (uid: string) => {
    try {
      const { error } = await supabase.from('profiles').delete().eq('id', uid);
      if (error) throw error;
    } catch (err) {
      handleSupabaseError(err, OperationType.DELETE, 'profiles');
    }
  };

  const handleChangeTier = async (uid: string, currentTier: UserTier) => {
    const newTier: UserTier = currentTier === 'trusted' ? 'standard' : 'trusted';
    try {
      const { error } = await supabase.from('profiles').update({ tier: newTier }).eq('id', uid);
      if (error) throw error;
    } catch (err) {
      handleSupabaseError(err, OperationType.UPDATE, 'profiles');
    }
  };

  const filteredEvents = allEvents.filter((event) => {
    const search = eventSearch.toLowerCase();
    const matchesSearch =
      event.title.toLowerCase().includes(search) ||
      (event.companyName?.toLowerCase().includes(search) ?? false);
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || event.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const filteredClients = approvedUsers
    .map((u) => ({ ...u, eventCount: allEvents.filter((e) => e.createdBy === u.uid).length }))
    .filter((u) => {
      const search = clientSearch.toLowerCase();
      return (
        (u.companyName || '').toLowerCase().includes(search) ||
        (u.email || '').toLowerCase().includes(search) ||
        (u.displayName || '').toLowerCase().includes(search)
      );
    });

  const tabs = [
    { id: 'pending_events' as const, label: 'Pending events', icon: Clock, count: pendingEvents.length },
    { id: 'all_events' as const, label: 'All events', icon: CalendarIcon, count: allEvents.length },
    { id: 'pending_clients' as const, label: 'Pending clients', icon: Users, count: pendingUsers.length },
    { id: 'approved_clients' as const, label: 'Approved clients', icon: ShieldCheck, count: approvedUsers.length },
    { id: 'analytics' as const, label: 'Analytics', icon: TrendingUp, count: 0 },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto sticker bg-white overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-[#0b0b0c]/15 bg-[#f3ecfc]/60">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 sticker-soft bg-[#9945ff] flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
              System management
            </p>
            <h2 className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-[#0b0b0c]">
              Admin dashboard
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 min-h-11 sm:min-h-9 rounded-full border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#9945ff] text-white'
                  : 'bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.count > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-5 px-1.5 rounded-full text-[10px] font-black ${
                    activeTab === tab.id ? 'bg-white text-[#9945ff]' : 'bg-[#0b0b0c] text-white'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {/* Pending Events */}
        {activeTab === 'pending_events' && (
          <div className="space-y-3">
            {pendingEvents.length === 0 ? (
              <div className="sticker-soft bg-[#f3ecfc]/60 text-center py-16">
                <div className="w-12 h-12 rounded-full border-[1.5px] border-[#0b0b0c] bg-white inline-flex items-center justify-center mb-3">
                  <Check className="w-6 h-6 text-[#0b0b0c]/55" />
                </div>
                <p className="font-bold text-xl uppercase tracking-tight text-[#0b0b0c]">All caught up</p>
                <p className="text-sm text-[#0b0b0c]/55 mt-1">No pending events to review.</p>
              </div>
            ) : (
              pendingEvents.map((event) => (
                <div
                  key={event.id}
                  className="sticker-soft bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-[#f3ecfc]/40 transition-colors"
                >
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <CategoryPill category={event.category} />
                      <StatusPill status="pending" />
                      {event.chain && (
                        <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
                          {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-lg uppercase tracking-tight text-[#0b0b0c]">
                      <TruncatedText text={event.title} maxLength={60} />
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-[#0b0b0c]/65">
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        {format(event.date, 'MMM d, yyyy')}
                        {event.time && ` · ${event.time}`}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        <TruncatedText text={event.companyName || 'Unknown'} maxLength={24} />
                      </span>
                      {renderSubmitterInfo(event.createdBy, event.source)}
                    </div>
                  </div>
                  <div className="flex flex-col min-[420px]:flex-row sm:items-center gap-2 shrink-0 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(event)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 h-11 sm:h-9 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApproveEvent(event.id);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 h-11 sm:h-9 rounded-full bg-[#14f195] text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:opacity-90"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setRejectionModalId(event.id);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 h-11 sm:h-9 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* All Events */}
        {activeTab === 'all_events' && (
          <div className="space-y-4">
            {allEvents.filter((e) => !CHAINS.includes(e.chain)).length > 0 && (
              <div className="sticker-soft bg-[#ffd24a] p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-[#0b0b0c] shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-[#0b0b0c]">Data integrity audit</h4>
                    <p className="text-[12px] text-[#0b0b0c]/65">
                      Found {allEvents.filter((e) => !CHAINS.includes(e.chain)).length} events with invalid chain values.
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const invalid = allEvents.filter((e) => !CHAINS.includes(e.chain));
                    await Promise.all(
                      invalid.map((ev) => supabase.from('events').update({ chain: 'General Crypto' }).eq('id', ev.id)),
                    );
                  }}
                  className="px-4 h-11 sm:h-9 rounded-full bg-[#0b0b0c] text-white text-[11px] font-bold uppercase tracking-wider hover:opacity-90"
                >
                  Set to "General Crypto"
                </button>
              </div>
            )}

            <div className="sticker-soft bg-[#f3ecfc]/40 p-3 flex flex-col md:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
                <input
                  type="text"
                  placeholder="Search events or companies..."
                  className="w-full h-11 sm:h-10 pl-10 pr-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                />
              </div>
              <select
                className="h-11 sm:h-10 px-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-bold focus:outline-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select
                className="h-11 sm:h-10 px-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-bold focus:outline-none"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All categories</option>
                {CATEGORY_META.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className="sticker-soft bg-white p-3 flex flex-col gap-3 hover:bg-[#f3ecfc]/40 transition-colors"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill status={event.status} />
                    <CategoryPill category={event.category} />
                    {event.chain && (
                      <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
                        {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
                      </span>
                    )}
                    {event.featured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 pill-outline rounded-full text-[10px] font-bold uppercase tracking-wider">
                        <Star className="w-3 h-3 fill-[#ffd24a] text-[#0b0b0c]" />
                        Featured
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-base uppercase tracking-tight text-[#0b0b0c] leading-tight">
                      <TruncatedText text={event.title} maxLength={70} />
                    </h4>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#0b0b0c]/65">
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        <TruncatedText text={event.companyName || 'Unknown'} maxLength={20} />
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(event.date, 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-[#0b0b0c]/10">
                    {renderSubmitterInfo(event.createdBy, event.source)}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedEvent(event)}
                        className="px-3 h-11 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors text-[11px] font-bold uppercase tracking-wider"
                      >
                        View
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingEvent(event);
                        }}
                        className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-white hover:bg-[#0b0b0c] hover:text-white flex items-center justify-center transition-colors"
                        title="Edit event"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(event.id);
                        }}
                        className="w-11 h-11 sm:w-8 sm:h-8 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6b4d] text-white hover:bg-[#ff6a6a] flex items-center justify-center transition-colors"
                        title="Delete event"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Clients */}
        {activeTab === 'pending_clients' && (
          <div className="space-y-3">
            <div className="sticker-soft bg-[#79c8ff] p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-[#0b0b0c] mt-0.5 shrink-0" />
              <p className="text-[12px] text-[#0b0b0c]/85 leading-relaxed">
                Only X-authenticated users can be upgraded to <strong>Trusted</strong>. Google users are limited to <strong>Standard</strong>.
              </p>
            </div>
            {pendingUsers.length === 0 ? (
              <div className="sticker-soft bg-[#f3ecfc]/60 text-center py-16">
                <p className="font-bold text-xl uppercase tracking-tight text-[#0b0b0c]">No pending clients</p>
              </div>
            ) : (
              pendingUsers.map((user) => (
                <div key={user.uid} className="sticker-soft bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 sticker-soft bg-[#f3ecfc] flex items-center justify-center shrink-0">
                      {user.authProvider === 'twitter' ? (
                        <Twitter className="w-5 h-5 text-[#9945ff]" />
                      ) : (
                        <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-[#0b0b0c] truncate">{user.displayName}</h3>
                        <StatusPill status="pending" />
                      </div>
                      <p className="text-[11px] text-[#0b0b0c]/65 truncate">@{user.companyName}</p>
                      <div className="mt-1 flex items-center gap-3 text-[10px] text-[#0b0b0c]/55 font-medium">
                        {user.authProvider === 'twitter' ? (
                          <span className="inline-flex items-center gap-1">
                            <Twitter className="w-3 h-3 text-[#9945ff]" />@{user.twitterHandle || 'X user'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            <TruncatedText text={user.email || ''} maxLength={28} />
                          </span>
                        )}
                        <span>· joined {format(user.createdAt, 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <button
                      onClick={() => handleApproveUser(user.uid, 'standard')}
                      className="px-3 h-11 sm:h-9 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      Approve standard
                    </button>
                    {user.authProvider === 'twitter' && (
                      <button
                        onClick={() => handleApproveUser(user.uid, 'trusted')}
                        className="inline-flex items-center gap-1.5 px-3 h-11 sm:h-9 rounded-full bg-[#14f195] text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:opacity-90"
                      >
                        <Zap className="w-3.5 h-3.5" />
                        Approve trusted
                      </button>
                    )}
                    <button
                      onClick={() => handleRejectUser(user.uid)}
                      className="w-11 h-11 sm:w-9 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6b4d] text-white hover:bg-[#ff6a6a] flex items-center justify-center transition-colors"
                      title="Reject"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Approved Clients */}
        {activeTab === 'approved_clients' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
              <input
                type="text"
                placeholder="Search approved clients..."
                className="w-full h-11 sm:h-10 pl-10 pr-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />
            </div>

            {filteredClients.map((user) => (
              <div key={user.uid} className="sticker-soft bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="relative">
                    <div className="w-12 h-12 sticker-soft bg-[#f3ecfc] flex items-center justify-center shrink-0">
                      {user.authProvider === 'twitter' ? (
                        <Twitter className="w-5 h-5 text-[#9945ff]" />
                      ) : (
                        <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
                      )}
                    </div>
                    <div
                      className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-[1.5px] border-[#0b0b0c] flex items-center justify-center ${
                        user.tier === 'trusted' ? 'bg-[#14f195]' : 'bg-[#ffd24a]'
                      }`}
                    >
                      {user.tier === 'trusted' ? (
                        <Zap className="w-3 h-3 text-[#0b0b0c]" />
                      ) : (
                        <ShieldCheck className="w-3 h-3 text-[#0b0b0c]" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-[#0b0b0c] truncate">{user.displayName}</h3>
                      <span
                        className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
                        style={{
                          backgroundColor: user.tier === 'trusted' ? '#14f195' : '#ffd24a',
                          color: '#0b0b0c',
                        }}
                      >
                        {user.tier} tier
                      </span>
                    </div>
                    <p className="text-[11px] text-[#0b0b0c]/65 truncate">@{user.companyName}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] text-[#0b0b0c]/55 font-medium">
                      {user.authProvider === 'twitter' ? (
                        <span className="inline-flex items-center gap-1">
                          <Twitter className="w-3 h-3 text-[#9945ff]" />@{user.twitterHandle}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          <TruncatedText text={user.email || ''} maxLength={28} />
                        </span>
                      )}
                      <span>· {user.eventCount} events</span>
                      <span>· joined {format(user.createdAt, 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {user.authProvider === 'twitter' && (
                    <button
                      onClick={() => handleChangeTier(user.uid, user.tier || 'standard')}
                      className="px-3 h-11 sm:h-9 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      {user.tier === 'trusted' ? 'Downgrade' : 'Upgrade trusted'}
                    </button>
                  )}
                  <button
                    onClick={() => handleRevokeUser(user.uid)}
                    className="px-3 h-11 sm:h-9 rounded-full bg-[#ff6b4d] text-white border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors"
                  >
                    Revoke access
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'analytics' && <AnalyticsView />}
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-[#0b0b0c]/40 z-[100] flex items-end sm:items-center justify-center safe-overlay">
          <div className="sticker bg-[#e9dffa] max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 sticker-soft bg-[#ff6a6a] inline-flex items-center justify-center mb-4">
              <AlertCircle className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              Delete event?
            </h3>
            <p className="text-sm text-[#0b0b0c]/65 mb-5">
              This action cannot be undone. The event will be permanently removed.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEvent(showDeleteConfirm)}
                className="flex-1 h-11 rounded-full bg-[#ff6b4d] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event (reuses AddEventModal) */}
      {editingEvent && (
        <AddEventModal
          isAdmin={true}
          editEvent={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSuccess={() => setEditingEvent(null)}
        />
      )}

      {/* Event Detail */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          submitter={selectedEvent.createdBy ? usersMap[selectedEvent.createdBy] : undefined}
          onClose={() => setSelectedEvent(null)}
          isAdmin
          currentUserUid={undefined}
          onEdit={(event) => {
            setEditingEvent(event);
            setSelectedEvent(null);
          }}
          onDelete={(eventId) => {
            setShowDeleteConfirm(eventId);
            setSelectedEvent(null);
          }}
          onApprove={(eventId) => {
            handleApproveEvent(eventId);
            setSelectedEvent(null);
          }}
          onReject={(eventId) => {
            setRejectionModalId(eventId);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Rejection Reason */}
      {rejectionModalId && (
        <div className="fixed inset-0 bg-[#0b0b0c]/40 z-[110] flex items-end sm:items-center justify-center safe-overlay">
          <div className="sticker bg-[#e9dffa] max-w-md w-full p-6">
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              Reject submission
            </h3>
            <p className="text-sm text-[#0b0b0c]/65 mb-4">
              Let the submitter know why this event was rejected (optional).
            </p>
            <textarea
              className="w-full px-4 py-3 rounded-2xl border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30 resize-none h-28 mb-4"
              placeholder="Optional reason..."
              maxLength={500}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => {
                  setRejectionModalId(null);
                  setRejectionReason('');
                }}
                className="flex-1 h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRejectEvent(rejectionModalId, rejectionReason)}
                className="flex-1 h-11 rounded-full bg-[#ff6b4d] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors"
              >
                Reject event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
